import React, { useEffect, useRef, useState } from "react";
import { Button, Grid, Message } from "semantic-ui-react";
import { useDispatch, useSelector } from "react-redux";
import pencil from "../../../assets/icons/edit-pencil-hover-icon.svg";
import { TEAL_CONTAINER_COLOUR } from "../../../css/styles";
import { Form, Formik } from "formik";
import ReactGA from "react-ga4";
import {
  addJobAlertPreferencesToFirestore,
  deleteDocFromFirestore,
  listenToJobSeekerJobAlertPreferencesFromFirestore,
  logErrorInFirestore,
  toggleSettingInDocument,
  updateJobAlertPreferencesOnFirestore,
} from "../../../app/firestore/firestoreService";
import {
  FIRESTORE_JOB_ALERT_PREFERENCES_COLLECTION,
  LOG_ERROR_CODES,
  QUERY_CODES,
} from "../../../app/firestore/firestoreConstants";
import { JOB_ALERT_PREFERENCES_INITIAL_FORM } from "../../jobSeeker/jobSeekerConstants";
import MySelectInput from "../../../app/common/form/MySelectInput";
import { getCountryProperties } from "../../data/countries";
import MyProTagInput from "../../../app/common/form/MyProTagInput";
import UxToggle from "../../../app/common/components/UxToggle";
import { usePrevious } from "../../../app/hooks/usePrevious";
import {
  JOB_ALERTS_CUSTOM_PREFERENCES_VALIDATION_SCHEMA,
  JOB_ALERTS_PHARMA_PREFERENCES_VALIDATION_SCHEMA,
} from "../../../js/constants/validationSchemas";
import { getDayMonth } from "../../../js/helpers/dateHelpers";
import Tags from "../../../app/common/components/Tags";
import trashHoverIcon from "../../../assets/icons/trash-hover-icon.svg";
import useFirestoreCollection from "../../../app/hooks/useFirestoreCollection";
import { fetchJobAlertPreferences } from "../../jobSeeker/jobSeekerActions";
import alerts from "../../../assets/icons/alerts.svg";
import isEqual from "lodash/isEqual";
import {
  CANDIDATE_CATEGORIES,
  getIndustryAndCategory,
} from "../../modes/helpers";

// mirror on server
export const CUSTOM_ALERT_TYPE = "CUSTOM_ALERT";
export const PHARMA_SHIFT = "PHARMA_SHIFT_ALERT";

export const WEEKLY_ALERT_FREQUENCY = "WEEKLY_ALERT";
export const TWICE_DAILY = "TWICE_DAILY_ALERT";

function JobAlert({
  jobAlert,
  handleActiveToggle,
  setSelectedAlert,
  setEditMode,
}) {
  const { responsive } = useSelector((state) => state.app);

  return (
    <div
      className="teal-box-container body-blue-text"
      style={{ marginBottom: 22 }}
    >
      <div className="grey-row-bar">
        <span>Saved: {getDayMonth(jobAlert.dateLastEdited)}</span>
        <span className="float-right center-flex">
          <UxToggle
            labelText={"Active"}
            checked={jobAlert.isActive}
            handleClick={() => handleActiveToggle(jobAlert)}
            style={responsive.isMobile ? { marginLeft: 10 } : {}}
          />
          <Button
            className={"overwrite-button icon-text-button"}
            content={<span className={"action-text"}>Edit</span>}
            icon={
              <img
                className={"icon-text-button-img"}
                src={pencil}
                style={{ width: 17, height: 12 }}
                alt="edit"
              />
            }
            onClick={() => {
              setSelectedAlert(jobAlert);
              setEditMode(jobAlert.type);
            }}
            style={{ margin: "0 12px 0 15px" }}
            type={"button"}
          />
        </span>
      </div>
      <div style={{ padding: 10 }}>
        {jobAlert.type === CUSTOM_ALERT_TYPE ? "Jobs" : "Shifts"} in{" "}
        {jobAlert.locations.join(", ")}
        {jobAlert.industries[0]
          ? " | " + getIndustryAndCategory(jobAlert.industries[0])
          : null}
        {jobAlert.industries[1]
          ? ", " + getIndustryAndCategory(jobAlert.industries[1])
          : null}
        {jobAlert.type === PHARMA_SHIFT && " | Pharmacists"}
      </div>
      <Tags tags={jobAlert.jobKeywords} style={{ padding: 10 }} />
    </div>
  );
}

export default function JobAlertsPane() {
  const formRef = useRef();
  const dispatch = useDispatch();

  document.title = "Job Alerts | Hello Radius";

  const { currentUserAccount } = useSelector((state) => state.account);
  const { jobAlertPreferences } = useSelector((state) => state.jobSeeker);
  const { isCookieConsent } = useSelector((state) => state.app);
  const { responsive } = useSelector((state) => state.app);

  const [editMode, setEditMode] = useState(null);
  const [formValues, setFormValues] = useState(
    JOB_ALERT_PREFERENCES_INITIAL_FORM
  );
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [isFormSubmitting, setIsFormSubmitting] = useState(false);

  useFirestoreCollection({
    shouldExecute:
      currentUserAccount && currentUserAccount.isJobSeeker === true,
    query: () =>
      listenToJobSeekerJobAlertPreferencesFromFirestore(currentUserAccount.id),
    data: (jobAlerts) => dispatch(fetchJobAlertPreferences(jobAlerts)),
    deps: [dispatch, currentUserAccount],
    queryCode: QUERY_CODES.listenToJobSeekerSavedJobsFromFirestore,
  });

  const initialPreferences = usePrevious(jobAlertPreferences) || [];
  useEffect(() => {
    if (initialPreferences === undefined) return;
    if (initialPreferences !== jobAlertPreferences) {
      setEditMode(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobAlertPreferences]);

  function sortAlert(alert) {
    return {
      locations: alert.locations.sort(),
      industries: alert.industries.sort(),
      jobKeywords: alert.jobKeywords.map((word) => word.toLowerCase()).sort(),
      type: alert.type,
    };
  }

  function isAlertSame(alert) {
    if (formValues.id) {
      return false;
    } else {
      return (
        !!formRef?.current &&
        isEqual(
          sortAlert(alert),
          sortAlert({ ...formRef.current.values, type: editMode })
        )
      );
    }
  }

  const isAlertAlready =
    formRef?.current &&
    jobAlertPreferences.filter((i) => isAlertSame(i)).length > 0;

  function closeForm() {
    setEditMode(null);
    setFormValues(JOB_ALERT_PREFERENCES_INITIAL_FORM);
    setSelectedAlert(null);
  }

  const cancelButton = (
    <Button
      className={"text-button"}
      style={{ marginRight: 12 }}
      content={"Cancel"}
      onClick={closeForm}
      type={"button"}
    />
  );

  function saveInput() {
    if (formRef.current) {
      formRef.current.handleSubmit();
    }
  }

  function validateForm() {
    if (!isFormComplete()) {
      formRef.current && formRef.current.validateForm();
    }
  }

  function isFormComplete() {
    let isComplete = false;
    if (editMode === CUSTOM_ALERT_TYPE) {
      isComplete =
        formValues.locations.length > 0 &&
        (formValues.industries.length > 0 || formValues.jobKeywords.length > 0);
    }
    if (editMode === PHARMA_SHIFT) {
      isComplete = formValues.locations.length > 0;
    }
    if (isComplete) {
      formRef.current && formRef.current.setErrors([]);
    }
    return isComplete;
  }

  async function handleActiveToggle(jobAlert) {
    await toggleSettingInDocument({
      dbCollection: FIRESTORE_JOB_ALERT_PREFERENCES_COLLECTION,
      id: jobAlert.id,
      key: "isActive",
      toggle: jobAlert.isActive,
    });
  }

  const ctaButton = (
    <div onMouseEnter={validateForm}>
      <Button
        className="overwrite-button cta-orange-form"
        style={{ margin: "2px 12px 2px 2px" }}
        content={"Save"}
        type="submit"
        onClick={() => saveInput()}
        loading={isFormSubmitting}
        disabled={isAlertAlready || !isFormComplete()}
      />
    </div>
  );

  const addNewButton = (
    <Button
      className={"overwrite-button icon-text-button"}
      style={{ margin: "2px 12px 2px 2px" }}
      content={
        <span className={"action-text"} style={{ verticalAlign: "middle" }}>
          Create an alert
        </span>
      }
      icon={
        <img
          className={"icon-text-button-img"}
          style={{ height: 16, verticalAlign: "middle" }}
          src={alerts}
          alt="edit"
        />
      }
      onClick={() => {
        setSelectedAlert(null);
        setEditMode(CUSTOM_ALERT_TYPE);
      }}
      type={"button"}
    />
  );

  const customAlerts = jobAlertPreferences.filter(
    (alert) => alert.type === CUSTOM_ALERT_TYPE
  );
  const pharmaAlerts = jobAlertPreferences.filter(
    (alert) => alert.type === PHARMA_SHIFT
  );

  return (
    <Grid>
      <Grid.Row style={{ height: 70 }} only={"computer tablet"}>
        <Grid.Column>
          <h2>Job Alerts</h2>
        </Grid.Column>
      </Grid.Row>
      <Grid.Row
        columns={2}
        style={{
          borderRadius: 6,
          backgroundColor: TEAL_CONTAINER_COLOUR,
          padding: "4px 0",
          height: 48,
          marginTop: responsive.isMobile ? 20 : 0,
        }}
      >
        <Grid.Column width={5} verticalAlign={"middle"}>
          {!!editMode && selectedAlert && (
            <Button
              className={"overwrite-button icon-text-button"}
              content={<span className={"action-text"}>Delete</span>}
              icon={
                <img
                  className={"icon-text-button-img"}
                  src={trashHoverIcon}
                  style={{ width: 12, height: 14 }}
                  alt="delete"
                />
              }
              onClick={() => {
                deleteDocFromFirestore({
                  dbCollection: FIRESTORE_JOB_ALERT_PREFERENCES_COLLECTION,
                  id: selectedAlert.id,
                }).then(() => console.log("alert deleted"));
                closeForm();
              }}
            />
          )}
        </Grid.Column>
        <Grid.Column
          width={11}
          textAlign="right"
          verticalAlign="middle"
          style={{ paddingRight: 1 }}
        >
          {!!editMode && (
            <div className={"center-flex float-right"}>
              {jobAlertPreferences && cancelButton}
              {ctaButton}
            </div>
          )}
          {!editMode && addNewButton}
        </Grid.Column>
      </Grid.Row>
      {!editMode && (
        <Grid.Row>
          <Grid.Column computer={11} tablet={13} mobile={16}>
            {customAlerts.length > 0 ? (
              customAlerts.map((jobAlert) => (
                <JobAlert
                  jobAlert={jobAlert}
                  key={jobAlert.id}
                  handleActiveToggle={handleActiveToggle}
                  setSelectedAlert={setSelectedAlert}
                  setEditMode={setEditMode}
                />
              ))
            ) : (
              <div style={{ padding: responsive.isMobile ? "0 10px" : 0 }}>
                <h3>Get alerts in your inbox</h3>
                <div>
                  To avoid missing an opportunity, create an alert to receive
                  weekly notifications.
                </div>
              </div>
            )}
            {pharmaAlerts.length > 0 ? (
              <div style={{ marginTop: 40 }}>
                <h4>Shift alert:</h4>
                {pharmaAlerts.map((jobAlert, index) => (
                  <JobAlert
                    jobAlert={jobAlert}
                    key={jobAlert.id}
                    handleActiveToggle={handleActiveToggle}
                    setSelectedAlert={setSelectedAlert}
                    setEditMode={setEditMode}
                  />
                ))}
              </div>
            ) : (
              <div style={{ padding: responsive.isMobile ? "0 10px" : 0 }}>
                <div
                  className={"teal-box"}
                  style={{
                    width: responsive.isMobile ? "90%" : "80%",
                    marginTop: 40,
                  }}
                >
                  <h3>Are you a pharmacist looking for a shift?</h3>
                  <div>
                    Get notified about new shifts as soon as they’re posted!
                  </div>
                  <Button
                    className="overwrite-button teal-inverse-button"
                    onClick={() => setEditMode(PHARMA_SHIFT)}
                    style={{
                      margin: "15px 0 0",
                    }}
                  >
                    Subscribe to shift alerts
                  </Button>
                </div>
              </div>
            )}
          </Grid.Column>
        </Grid.Row>
      )}
      {!!editMode && (
        <Grid.Row>
          <Grid.Column
            computer={9}
            tablet={11}
            mobile={16}
            style={{ paddingRight: 0, transition: "all ease 0.5s" }}
          >
            <Formik
              initialValues={
                selectedAlert || JOB_ALERT_PREFERENCES_INITIAL_FORM
              }
              innerRef={formRef}
              // validateOnChange={index !== STEP_1}
              validateOnChange={false}
              validateOnBlur={false}
              validateOnMount={false}
              validationSchema={
                editMode === CUSTOM_ALERT_TYPE
                  ? JOB_ALERTS_CUSTOM_PREFERENCES_VALIDATION_SCHEMA
                  : JOB_ALERTS_PHARMA_PREFERENCES_VALIDATION_SCHEMA
              }
              onSubmit={async (values, { setSubmitting, setErrors }) => {
                setIsFormSubmitting(true);

                const jobSeekerId = currentUserAccount.id;
                const dateToday = new Date();
                let logRef = null;

                let step = 1;

                try {
                  if (isCookieConsent) {
                    ReactGA.event({
                      category: "Metrics",
                      action: "Company Profile saving",
                    });
                  }

                  if (!selectedAlert) {
                    // initial submit
                    await addJobAlertPreferencesToFirestore({
                      jobKeywords: values.jobKeywords,
                      industries: values.industries,
                      locations: values.locations,
                      jobSeekerId: currentUserAccount.id,
                      countryCode: currentUserAccount.countryCode,
                      email: currentUserAccount.email,
                      displayName: currentUserAccount.displayName,
                      dateToday,
                      type: editMode,
                      frequency:
                        editMode === CUSTOM_ALERT_TYPE
                          ? WEEKLY_ALERT_FREQUENCY
                          : TWICE_DAILY,
                    });
                  } else {
                    // edit
                    await updateJobAlertPreferencesOnFirestore({
                      preferencesId: selectedAlert.id,
                      jobKeywords: values.jobKeywords,
                      industries: values.industries,
                      locations: values.locations,
                      dateToday: dateToday,
                    });
                  }
                } catch (e) {
                  setErrors({
                    auth: e.message || "Something went wrong..",
                  });
                  await logErrorInFirestore({
                    error: e.toString(),
                    errorPage: "Candidate profile form",
                    errorCode: LOG_ERROR_CODES.candidateProfiles,
                    notes: {
                      jobSeekerId: jobSeekerId,
                      email: currentUserAccount.email,
                      ref: logRef,
                      step: step,
                    },
                  });
                } finally {
                  setSubmitting(false);
                  setIsFormSubmitting(false);
                  closeForm();
                }
              }}
            >
              {({ values }) => {
                setFormValues(values);
                return (
                  <Form className="ui form post-job-carousel-form">
                    <h3 style={{ marginBottom: 30 }}>
                      {!selectedAlert &&
                        editMode === CUSTOM_ALERT_TYPE &&
                        "Create an alert"}
                      {!selectedAlert &&
                        editMode === PHARMA_SHIFT &&
                        "Create a shift alert"}
                      {selectedAlert?.type === CUSTOM_ALERT_TYPE &&
                        "Edit job alert"}
                      {selectedAlert?.type === PHARMA_SHIFT &&
                        "Edit shift alert"}
                    </h3>
                    {isAlertAlready && (
                      <Message
                        negative
                        size="mini"
                        style={{
                          backgroundColor: "white",
                          padding: "10px 13px",
                          width: "fit-content",
                          margin: "0 0 19px 0",
                        }}
                        content={
                          <div
                            className="error"
                            style={{
                              fontSize: 15,
                            }}
                          >
                            This alert already exists.
                          </div>
                        }
                      />
                    )}
                    {editMode === PHARMA_SHIFT && (
                      <div
                        className={"body-blue-text"}
                        style={{ marginBottom: 15 }}
                      >
                        Select cities where you are looking for shifts:
                      </div>
                    )}
                    <MySelectInput
                      style={{ width: "70%" }}
                      className="form-input-field"
                      options={
                        getCountryProperties(currentUserAccount.countryCode)
                          .selectLocationOptions
                      }
                      name={"locations"}
                      clearable
                      multiple
                      placeholder={"Job location*"}
                      isShowErrorBeforeTouch={true}
                    />
                    {editMode === CUSTOM_ALERT_TYPE && (
                      <MySelectInput
                        style={{ width: "70%" }}
                        className="form-input-field"
                        name="industries"
                        placeholder="Categories (max. 2)"
                        search
                        options={CANDIDATE_CATEGORIES}
                        multiple={true}
                        limit={2}
                        isShowErrorBeforeTouch={true}
                        // isDisabled={!currentUserAccount.isAdmin}
                      />
                    )}
                    <br />
                    {editMode === CUSTOM_ALERT_TYPE && (
                      <div className={"body-blue-text"}>
                        <h3>Set preferences</h3>
                        What to include:
                        <div style={{ marginBottom: 20 }}>
                          <ul style={{ margin: "7px 0" }}>
                            <li key={1}>Job title/experience level</li>
                            <li key={2}>Areas of interest/technologies</li>
                          </ul>
                        </div>
                        <div style={{ marginBottom: 9 }}>Add keyword(s)</div>
                        <MyProTagInput
                          name="jobKeywords"
                          formTags={values.jobKeywords}
                          placeholder="Press enter to add"
                          maxTags={6}
                          inputWidth={"61%"}
                          isShowCount={true}
                          examplesText={"Customise your alerts with keywords"}
                          examplesHeading={""}
                          examples={[
                            "Freelance",
                            "Accountant",
                            "Barista",
                            "Digital Marketing",
                            "JavaScript",
                          ]}
                          showError={true}
                          isShowErrorBeforeTouch={true}
                        />
                      </div>
                    )}
                    <br />
                    <div className={"body-blue-text clear-above"}>
                      Notifications will be sent to{" "}
                      <span className={"semi-bold"}>
                        {currentUserAccount.email}
                      </span>
                      <br /> when jobs matching your alert are posted
                    </div>
                  </Form>
                );
              }}
            </Formik>
          </Grid.Column>
        </Grid.Row>
      )}
    </Grid>
  );
}
